import { useEffect, useState } from "react";
import { Star, BadgeCheck, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function Stars({ value, size = 16, onChange, testid }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testid} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} onClick={onChange ? () => onChange(n) : undefined} data-testid={onChange ? `${testid}-${n}` : undefined}
          className={`${n <= Math.round(value * 2) / 2 ? "fill-[#D4A359] text-[#D4A359]" : n - 0.5 === Math.round(value * 2) / 2 ? "fill-[#D4A359]/40 text-[#D4A359]" : "text-[#C9C2B8]"} ${onChange ? "cursor-pointer" : ""}`}/>
      ))}
    </span>
  );
}

const BLANK = { name: "", email: "", location: "", rating: 5, title: "", text: "", safari_name: "", trip_date: "", booking_ref: "", mobile: "" };

function ReviewForm({ onDone }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const cls = "h-10 w-full rounded-md border border-[#E2DACF] bg-white px-3 text-sm";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.text.trim().length < 20) return toast.error("Please write at least 20 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", f);
      toast.success(data.verified ? "Thanks! Your verified review will appear once approved." : "Thanks! Your review will appear once approved.");
      onDone();
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="review-form">
      <div className="flex items-center gap-3"><span className="text-sm">Your rating</span><Stars value={f.rating} size={24} onChange={(n) => setF({ ...f, rating: n })} testid="review-rating"/></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <input required className={cls} placeholder="Your name" value={f.name} onChange={set("name")} data-testid="review-name"/>
        <input required type="email" className={cls} placeholder="Email (not shown)" value={f.email} onChange={set("email")} data-testid="review-email"/>
        <input className={cls} placeholder="City / country" value={f.location} onChange={set("location")} data-testid="review-location"/>
        <input className={cls} placeholder="Safari you took (optional)" value={f.safari_name} onChange={set("safari_name")} data-testid="review-safari"/>
      </div>
      <input className={cls} placeholder="Review title" value={f.title} onChange={set("title")} data-testid="review-title"/>
      <textarea required rows={4} maxLength={1500} className="w-full rounded-md border border-[#E2DACF] p-3 text-sm" placeholder="Tell other travellers about your trip" value={f.text} onChange={set("text")} data-testid="review-text"/>
      <div className="rounded-lg bg-[#F5F0EB] p-3">
        <div className="text-xs text-[#5C6B63] mb-2 flex items-center gap-1"><BadgeCheck size={13}/> Booked with us? Add your booking reference and mobile to get a "Verified guest" badge.</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input className={cls} placeholder="Booking ref (JT-…)" value={f.booking_ref} onChange={set("booking_ref")} data-testid="review-ref"/>
          <input className={cls} placeholder="Mobile used for booking" value={f.mobile} onChange={set("mobile")} data-testid="review-mobile"/>
        </div>
      </div>
      <button disabled={busy} className="jt-pill-primary disabled:opacity-50" data-testid="review-submit">{busy ? "Sending…" : "Submit review"}</button>
    </form>
  );
}

export function ReviewsSection({ onLoad }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/reviews").then((r) => { setData(r.data); onLoad?.(r.data); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const avg = data.average ?? data.sample_average;

  return (
    <section className="jt-container py-20" data-testid="reviews-section">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="jt-eyebrow mb-3">Guest reviews</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A2B]">What guests say</h2>
          {avg && (
            <div className="mt-3 flex items-center gap-3 text-sm text-[#3B4A42]" data-testid="reviews-summary">
              <span className="font-serif text-3xl text-[#1E3A2B]">{Number(avg).toFixed(1)}</span><Stars value={avg} size={18}/>
              <span>{data.count ? `${data.count} review${data.count > 1 ? "s" : ""}` : "sample reviews"}</span>
            </div>
          )}
        </div>
        <button onClick={() => setOpen(true)} className="jt-pill-outline" data-testid="reviews-write-btn"><PenLine size={14}/> Write a review</button>
      </div>
      {data.reviews.length > 0 && (
        <div className="grid gap-6 md:grid-cols-3">
          {data.reviews.slice(0, 6).map((r, i) => (
            <article key={r.id} className={`bg-white ring-1 ring-black/5 rounded-2xl p-6 flex flex-col ${i % 3 === 1 ? "md:translate-y-6" : ""}`} data-testid={`review-card-${i}`}>
              <Stars value={r.rating}/>
              {r.title && <h3 className="font-serif text-lg text-[#1C2421] mt-3">{r.title}</h3>}
              <p className="text-sm text-[#3B4A42] leading-relaxed mt-2 flex-1">&ldquo;{r.text}&rdquo;</p>
              {r.reply && <p className="text-xs text-[#5C6B63] mt-3 border-l-2 border-[#C86D51] pl-3"><strong>JawaiTrails:</strong> {r.reply}</p>}
              <div className="mt-4 text-xs text-[#5C6B63] flex items-center gap-2 flex-wrap">
                <span className="font-medium text-[#1C2421]">{r.name}</span>{r.location && <span>· {r.location}</span>}
                {r.verified && <span className="inline-flex items-center gap-1 text-[#1E3A2B]"><BadgeCheck size={12}/> Verified guest</span>}
                {r.is_sample && <span className="px-2 py-0.5 rounded-full bg-[#F2C46D]/50 text-[#1C2421]" data-testid={`review-sample-${i}`}>Sample</span>}
              </div>
              {(r.safari_name || r.trip_date) && <div className="text-[11px] text-[#8A958F] mt-1">{[r.safari_name, r.trip_date].filter(Boolean).join(" · ")}</div>}
            </article>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#1E3A2B]">Share your Jawai trip</DialogTitle></DialogHeader>
          <ReviewForm onDone={() => setOpen(false)}/>
        </DialogContent>
      </Dialog>
    </section>
  );
}
